import React from 'react';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, NamedLink, Page } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { blogArticles, blogHub } from '../../data/blogSeoPages';
import css from './BlogHubPage.module.css';

const createAbsoluteUrl = (rootUrl, path) => `${(rootUrl || '').replace(/\/$/, '')}${path}`;

const createBlogHubSocialSharing = (rootUrl, featuredArticle) => {
  const imageUrl = createAbsoluteUrl(rootUrl, featuredArticle.heroImage);
  const image = {
    url: imageUrl,
    width: 1536,
    height: 1024,
    alt: featuredArticle.heroAlt,
  };

  return {
    title: blogHub.metaTitle,
    description: blogHub.metaDescription,
    twitterCard: 'summary_large_image',
    images1200: [image],
    images600: [image],
  };
};

const buildBlogHubSchema = (rootUrl, articles) => {
  const hubUrl = `${rootUrl}${blogHub.path}`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: blogHub.title,
      description: blogHub.metaDescription,
      url: hubUrl,
      hasPart: articles.map(article => ({
        '@type': 'BlogPosting',
        headline: article.title,
        url: `${rootUrl}${article.path}`,
        image: createAbsoluteUrl(rootUrl, article.heroImage),
        datePublished: article.publishedDate,
        dateModified: article.updatedDate,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: rootUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: hubUrl,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'FixitTask blog articles',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${rootUrl}${article.path}`,
        name: article.title,
      })),
    },
  ];
};

const BlogHubPage = () => {
  const config = useConfiguration();
  const rootUrl = config?.marketplaceRootURL || 'https://fixittask.com';
  const canonicalUrl = `${rootUrl}${blogHub.path}`;
  const featuredArticle = blogArticles[0];
  const articles = blogArticles;

  return (
    <Page
      title={blogHub.metaTitle}
      description={blogHub.metaDescription}
      socialSharing={createBlogHubSocialSharing(rootUrl, featuredArticle)}
      schema={buildBlogHubSchema(rootUrl, articles)}
      className={css.root}
    >
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <main className={css.page}>
          <section className={css.hero}>
            <div className={css.heroCopy}>
              <nav className={css.breadcrumbs} aria-label="Breadcrumb">
                <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                  Home
                </NamedLink>
                <span aria-hidden="true">/</span>
                <span>Blog</span>
              </nav>

              <p className={css.eyebrow}>{blogHub.eyebrow}</p>
              <h1 className={css.title}>{blogHub.title}</h1>
              <p className={css.lead}>{blogHub.lead}</p>
            </div>

            <NamedLink
              name="BlogArticlePage"
              params={{ articleSlug: featuredArticle.slug }}
              className={css.featuredCard}
            >
              <img
                className={css.featuredImage}
                src={featuredArticle.heroImage}
                alt={featuredArticle.heroAlt}
                decoding="async"
              />
              <div className={css.featuredCopy}>
                <p className={css.cardMeta}>
                  Featured guide / {featuredArticle.readTime}
                </p>
                <h2>{featuredArticle.title}</h2>
                <p>{featuredArticle.metaDescription}</p>
                <span className={css.readLink}>Read the guide</span>
              </div>
            </NamedLink>
          </section>

          <section className={css.section}>
            <div className={css.sectionHeader}>
              <p className={css.eyebrow}>Latest guides</p>
              <h2>Practical help for everyday home projects</h2>
              <p>
                These articles are written to be useful before you book help, buy parts, or start a
                small repair yourself. No mystery, no contractor jargon, just the parts that matter.
              </p>
            </div>

            <div className={css.articleGrid}>
              {articles.map(article => (
                <NamedLink
                  name="BlogArticlePage"
                  params={{ articleSlug: article.slug }}
                  className={css.articleCard}
                  key={article.slug}
                >
                  <img
                    className={css.articleImage}
                    src={article.heroImage}
                    alt={article.heroAlt}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className={css.articleCopy}>
                    <p className={css.cardMeta}>
                      {article.eyebrow} / {article.readTime}
                    </p>
                    <h3>{article.title}</h3>
                    <p>{article.metaDescription}</p>
                    <span className={css.readLink}>Open article</span>
                  </div>
                </NamedLink>
              ))}
            </div>
          </section>

          <section className={css.ctaSection}>
            <div>
              <p className={css.eyebrow}>Need local help?</p>
              <h2>Compare handyman providers on FixitTask</h2>
              <p>
                If a repair is bigger than expected, you can browse local handyman listings,
                compare prices and reviews, and message providers before booking.
              </p>
            </div>
            <NamedLink name="HandymanPage" className={css.primaryButton}>
              Browse handyman help
            </NamedLink>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BlogHubPage;
